import { createContext } from "react";

/**
 * The bottom tab currently held down, kept OUTSIDE React state.
 *
 * The tab bar is always-mounted chrome, so a press must not re-render the navigator, its scene
 * wrappers or the other tab items — only the pressed icon cares. Same reasoning (and shape) as
 * the optimistic active-tab store in `routes/nav/views/tabs`.
 */
export type PressedPrimaryNavigationDestinationStore = {
  getSnapshot: () => string | null;
  /** Called on press-in. */
  press: (name: string) => void;
  /** Called on press-out/cancel. Ignores a stale release from a tab that is no longer held. */
  release: (name: string) => void;
  subscribe: (listener: () => void) => () => void;
};

export function createPressedPrimaryNavigationDestinationStore(): PressedPrimaryNavigationDestinationStore {
  let pressedName: string | null = null;
  const listeners = new Set<() => void>();
  const publish = (next: string | null) => {
    if (pressedName === next) {
      return;
    }
    pressedName = next;
    for (const listener of listeners) {
      listener();
    }
  };
  return {
    getSnapshot: () => pressedName,
    press: (name) => {
      publish(name);
    },
    release: (name) => {
      // Two buttons can overlap a press (a drag off one onto another). Only the tab that is
      // actually held may clear the store, or the release of an abandoned press would cancel
      // the press that replaced it.
      if (pressedName !== name) {
        return;
      }
      publish(null);
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/**
 * Exposes {@link PressedPrimaryNavigationDestinationStore} to the tab icons. `null` outside the tab bar (previews,
 * tests, the drawer), where nothing publishes presses and the icons simply never look pressed.
 */
export const PressedPrimaryNavigationDestinationStoreContext =
  createContext<PressedPrimaryNavigationDestinationStore | null>(null);
